import { Moon, Sun } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useLocation } from 'react-router-dom';

import { haptics } from '@shared/lib/telegram';
import { useGetProductsQuery } from '@entities/product';
import { getThemeMode, setThemeMode, type ThemeMode } from '@shared/lib/theme';
import { EXCLUSIVE_CAMPAIGN_ENDS_AT } from '@shared/config/exclusiveCampaign';
import { MeskelBrand } from './MeskelBrand';
import styles from './TopAppBar.module.css';

function titleForPath(pathname: string): string {
  if (pathname === '/') return 'YeneShop';
  if (pathname.startsWith('/products/orders/')) return 'Deliver order';
  if (pathname.startsWith('/panel/analytics')) return 'Money analytics';
  if (pathname.startsWith('/panel/users/')) return 'Customer';
  if (pathname.startsWith('/panel/resellers')) return 'Resellers';
  if (pathname.startsWith('/wallet')) return 'Wallet';
  if (pathname.startsWith('/orders')) return 'Orders';
  if (pathname.startsWith('/invite')) return 'Invite & Earn';
  if (pathname.startsWith('/reseller')) return 'Reseller';
  if (pathname.startsWith('/products')) return 'Products';
  if (pathname.startsWith('/panel')) return 'Panel';
  return 'YeneShop';
}

/** Persistent app bar shared by every route rendered above the bottom bar. */
export function TopAppBar() {
  const { pathname } = useLocation();
  const lastScroll = useRef(0);
  const rippleId = useRef(0);
  const [hidden, setHidden] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => getThemeMode());
  const [meskelActive, setMeskelActive] = useState(
    () => Date.now() < Date.parse(EXCLUSIVE_CAMPAIGN_ENDS_AT),
  );
  const [ripple, setRipple] = useState<{ x: number; y: number; id: number } | null>(null);
  const isDark =
    theme === 'dark' ||
    (theme === 'telegram' && document.documentElement.dataset.theme === 'dark');
  const title = titleForPath(pathname);
  const storefront = title === 'YeneShop';
  const { data: catalog } = useGetProductsQuery(undefined, { skip: !storefront });
  const clockOffsetMs = catalog
    ? Date.parse(catalog.serverTime) - catalog.clientReceivedAt
    : 0;

  useEffect(() => {
    setHidden(false);
    lastScroll.current = Math.max(0, window.scrollY);
  }, [pathname]);

  useEffect(() => {
    const endsAt = Date.parse(EXCLUSIVE_CAMPAIGN_ENDS_AT);
    const update = () => setMeskelActive(Date.now() + clockOffsetMs < endsAt);
    update();
    const timeout = window.setTimeout(
      update,
      Math.max(0, endsAt - (Date.now() + clockOffsetMs) + 50),
    );
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener('visibilitychange', update);
    };
  }, [clockOffsetMs]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const current = Math.max(0, window.scrollY);
      const difference = current - lastScroll.current;

      if (current < 12) {
        setHidden(false);
        lastScroll.current = current;
      } else if (difference > 5) {
        setHidden(true);
        lastScroll.current = current;
      } else if (difference < -5) {
        setHidden(false);
        lastScroll.current = current;
      }
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const showThemeRipple = (event: PointerEvent<HTMLButtonElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setRipple({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
      id: rippleId.current++,
    });
  };

  return (
    <header className={hidden ? styles.barHidden : styles.bar}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.h1
          key={title}
          className={storefront ? styles.brand : styles.pageTitle}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.16, ease: [0.2, 0, 0, 1] }}
        >
          {storefront && meskelActive ? <MeskelBrand paused={hidden} /> : title}
        </motion.h1>
      </AnimatePresence>

      <div className={styles.actions}>
        <button
          className={styles.themeAction}
          type="button"
          aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
          title={isDark ? 'Use light theme' : 'Use dark theme'}
          onPointerDown={showThemeRipple}
          onClick={() => {
            const next: ThemeMode = isDark ? 'light' : 'dark';
            haptics.select();
            setThemeMode(next);
            setTheme(next);
          }}
        >
          {ripple ? (
            <span
              key={ripple.id}
              className={styles.ripple}
              style={{ left: ripple.x, top: ripple.y }}
              aria-hidden="true"
            />
          ) : null}
          {isDark ? (
            <Sun size={20} weight="fill" />
          ) : (
            <Moon size={20} weight="fill" />
          )}
        </button>
      </div>
    </header>
  );
}
